import { runtimeConfig } from '@/config/runtime';
import type { AiLang, AiSite } from '@/types/runtime-config';
import { post } from './api';

/** AI Search 支持的业务场景和官网站点。 */
export type ChatIntent = 'product_search' | 'image_search' | 'showroom_qa' | 'open_qa';
export type ChatSite = AiSite;
export type SearchStatus = 'success' | 'no_result' | 'degraded' | 'invalid' | 'error';

/** 统一聊天接口允许传入的字段。 */
export interface ChatRequest {
  text?: string;
  image?: string;
  intent?: ChatIntent;
  site?: ChatSite;
  limit?: number;
  conversation_id?: string;
  current_product_id?: string;
  lang: AiLang;
}

/** 商品结果保留文档中的常用字段，并兼容后端后续扩展字段。 */
export interface ChatProduct {
  product_id: string;
  erp_no?: string;
  product_name: string;
  product_name_en?: string;
  product_name_cn?: string;
  category?: string;
  category_cn?: string;
  main_image: string;
  description?: string;
  color?: string;
  material?: string;
  specification?: string;
  intl_url?: string;
  us_url?: string;
  cn_url?: string;
  intl_stage_url?: string;
  us_stage_url?: string;
  cn_stage_url?: string;
  matched_tags?: string[];
  score?: number;
  score_details?: unknown[];
  reason?: string;
  [key: string]: unknown;
}

/** 展厅查询结果的数据结构。 */
export interface ChatShowroom {
  showroom_id: string;
  name: string;
  city?: string;
  country?: string;
  address?: string;
  phone?: string;
  hours?: string;
  description?: string;
  brands?: string[];
  services?: string[];
  status?: string;
  [key: string]: unknown;
}

/** 统一业务接口附带的结果统计和业务状态。 */
export interface ChatMeta {
  result_count: number;
  search_status: SearchStatus;
}

/** 统一业务接口的 data 内容。 */
export interface ChatData {
  request_id: string;
  run_id: string;
  trace_id: string;
  conversation_id: string;
  intent: ChatIntent;
  answer: string;
  products: ChatProduct[];
  showrooms: ChatShowroom[];
  kb_source_ids: string[];
  suggested_questions: string[];
  explanation: Record<string, unknown>;
  meta: ChatMeta;
}

/** 接口统一响应外壳。 */
export interface ChatResponse {
  code: string;
  message: string;
  data: ChatData;
}

/** 将页面当前语言转换为 AI Search 所需的站点值。 */
export const getChatSiteByLanguage = (lang: AiLang = runtimeConfig.lang): ChatSite => {
  if (runtimeConfig.site) return runtimeConfig.site;
  if (lang === 'ch') return 'cn';
  return 'intl';
};

/** 在前端提前发现明显参数错误；真正的安全校验仍由 WordPress 服务端完成。 */
const validateChatRequest = (payload: ChatRequest): void => {
  if (!payload.text?.trim() && !payload.image) {
    throw new TypeError('text 和 image 至少需要传入一个。');
  }
  if (payload.limit !== undefined && (payload.limit < 1 || payload.limit > 20)) {
    throw new RangeError('limit 必须在 1 到 20 之间。');
  }
  if (payload.intent === 'image_search' && !payload.image) {
    throw new TypeError('图片搜索必须传入 image。');
  }
  if ((payload.intent === 'product_search' || payload.intent === 'image_search') && !payload.site) {
    throw new TypeError('产品和图片搜索必须传入 site。');
  }
  if ((payload.intent === 'showroom_qa' || payload.intent === 'open_qa') && payload.site) {
    throw new TypeError('展厅和知识问答不能传入 site。');
  }
};

/** 调用 WordPress 同源代理发起聊天请求，前端不会接触 AI Search Token。 */
export const sendChatMessage = (
  payload: ChatRequest,
  signal?: AbortSignal,
): Promise<ChatResponse> => {
  validateChatRequest(payload);
  return post<ChatResponse, ChatRequest>(runtimeConfig.chatApiUrl, payload, { signal });
};

/** 将用户选择的图片转换成接口要求的 Data URL，并做 5 MB 的体验层校验。 */
export const imageFileToDataUrl = (file: File): Promise<string> => {
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
  if (!allowedTypes.includes(file.type)) {
    return Promise.reject(new TypeError('只支持 JPEG、PNG、WebP 和 GIF 图片。'));
  }
  if (file.size > 5 * 1024 * 1024) {
    return Promise.reject(new RangeError('图片大小不能超过 5 MB。'));
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => typeof reader.result === 'string'
      ? resolve(reader.result)
      : reject(new TypeError('图片读取失败。'));
    reader.onerror = () => reject(new TypeError('图片读取失败。'));
    reader.readAsDataURL(file);
  });
};
